/**
 * Chart-touch JS handler (Patch DT). Defense-in-depth alongside
 * chart-touch.css's `touch-action: none` rule.
 *
 * Rationale: chart-touch.css declares `touch-action: none` on
 * `.recharts-wrapper`, which is supposed to make the browser
 * surrender all gestures inside the chart to the chart's own event
 * handlers. In practice this works inconsistently on iOS Safari PWA
 * across timeframes and chart types - particularly the Power Law
 * chart on the Assumptions tab, where users have reported that the
 * tooltip only follows a drag if the drag starts inside the tooltip
 * display box itself rather than anywhere on the chart.
 *
 * This file adds a second layer of defense: a non-passive global
 * touchmove listener that calls preventDefault() whenever the touch
 * gesture started inside a `.recharts-wrapper`. preventDefault on a
 * non-passive touchmove tells the browser to abandon any in-progress
 * scroll/zoom interpretation of the gesture, regardless of what
 * touch-action says.
 *
 * Why both CSS and JS:
 *   - CSS touch-action is the W3C-blessed primitive and handles the
 *     gesture-classification decision at the earliest point in the
 *     event pipeline. When it works, it works perfectly.
 *   - JS preventDefault runs later in the pipeline but is also
 *     respected by the browser. iOS Safari has had bugs around
 *     touch-action: none in PWA contexts (radar history goes back
 *     to iOS 13ish; some scenarios got fixed in iOS 15, others
 *     still surface). preventDefault on a non-passive touchmove is
 *     the historical workaround that browsers have honored since
 *     before touch-action existed.
 *
 * What this does NOT fix:
 *   - It does not make recharts' tooltip-detection area bigger. If
 *     recharts internally only triggers tooltips on touches within
 *     its computed cartesian-grid bounds, touches outside those
 *     bounds still do nothing - they just don't scroll either.
 *   - It does not affect non-chart touches at all. The listener is
 *     a no-op when the touchstart target is not inside a chart.
 *
 * Cost: one global touchstart + touchmove + touchend listener.
 * touchstart is passive (zero scroll-blocking cost on non-chart
 * touches), touchmove is non-passive but only does work when
 * touchOnChart is true (cheap branch otherwise), touchend just
 * resets a boolean.
 */

// Module-scoped state. Set in touchstart, read in touchmove, cleared
// in touchend / touchcancel. We do not need to distinguish multiple
// concurrent touches because the failure mode this fixes is browser
// taking over a SINGLE finger's drag for scroll; multi-touch gestures
// (pinch zoom) are blocked separately by CSS touch-action: none and
// the SVG element having no zoomable content.
let touchOnChart = false;

if (typeof document !== 'undefined') {
  document.addEventListener(
    'touchstart',
    (e) => {
      const target = e.target as Element | null;
      // .closest walks up the DOM tree from the touch target. If any
      // ancestor (or the target itself) has class recharts-wrapper,
      // this touch is on a chart. Note: closest correctly handles
      // touches that land on SVG child elements (lines, dots, axis
      // ticks) because those bubble up to the wrapper div.
      touchOnChart = !!target?.closest?.('.recharts-wrapper');
    },
    { passive: true },
  );

  document.addEventListener(
    'touchmove',
    (e) => {
      // preventDefault here is the actual fix. It tells the browser
      // not to scroll/zoom for this gesture. Must be non-passive
      // (the third arg to addEventListener) for preventDefault to
      // actually take effect on touchmove - the default is passive
      // for performance, but that means preventDefault is silently
      // ignored.
      if (touchOnChart) e.preventDefault();
    },
    { passive: false },
  );

  const clearTouch = (): void => {
    touchOnChart = false;
  };
  document.addEventListener('touchend', clearTouch, { passive: true });
  document.addEventListener('touchcancel', clearTouch, { passive: true });
}

// No exports - this module is side-effect-only, imported for the
// listener registration above.
export {};
